import {Box, Flex, Text, Title} from 'exsportia-components'
import {FC, useState} from 'react'
import {useParams} from 'react-router-dom'

import {CardBucket, cardUiState, isExpiringSoon} from '../../api/promo-codes/card-state'
import {useMyPromos} from '../../api/promo-codes/my-promos'
import {useVenue} from '../../api/venue/hooks'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {useAuthState} from '../../services/auth/hooks'
import {theme} from '../../theme/theme'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {AddCodeForm} from './add-code-form'
import {GhostPromoCard, PromoCard} from './promo-card'

const TABS: {id: CardBucket; label: 'vouchers_tab_expired' | 'vouchers_tab_ready' | 'vouchers_tab_used'}[] = [
  {id: 'ready', label: 'vouchers_tab_ready'},
  {id: 'used', label: 'vouchers_tab_used'},
  {id: 'expired', label: 'vouchers_tab_expired'},
]

// Profile menu -> "Vouchers & codes": this venue's saved promotions in Ready / Used / Expired,
// and a field to add a code the customer was given.
export const VouchersPage: FC = () => {
  const {l} = useLocale()
  const {venueId} = useParams()
  const {isLoggedIn, signIn} = useAuthState(venueId)
  const {data: venue} = useVenue()
  const {data, isLoading} = useMyPromos(venueId)
  const [tab, setTab] = useState<CardBucket>('ready')
  const total = data.ready.length + data.used.length + data.expired.length
  const hasExpiringSoon = data.ready.some((card) => isExpiringSoon(card, 'ready'))

  return (
    <GridLayout>
      <Box width='100%'>
        <Box py='8px'>
          <Title
            styleType='h2'
            text={l('vouchers_title')}
          />
        </Box>
        {!isLoggedIn ? (
          <Flex
            py='24px'
            gap='12px'
            flexDirection='column'
          >
            <Text
              text={l('vouchers_sign_in')}
              settings={{color: theme.colors['text-secondary']}}
            />
            <Button
              type='primary'
              onClick={() => signIn()}
            >
              {l('signIn')}
            </Button>
          </Flex>
        ) : isLoading ? (
          <Box py='24px'>
            <Text
              text={l('loading')}
              settings={{color: theme.colors['text-secondary']}}
            />
          </Box>
        ) : (
          <>
            {total === 0 ? (
              <Box py='12px'>
                <GhostPromoCard
                  title={l('vouchers_empty_title')}
                  body={venue?.name ? l('vouchers_empty_body_venue', {venue: venue.name}) : l('vouchers_empty_body')}
                />
              </Box>
            ) : (
              <>
                <Flex
                  gap='8px'
                  py='8px'
                >
                  {TABS.map((item) => (
                    <Button
                      key={item.id}
                      type={tab === item.id ? 'primary' : 'secondary'}
                      onClick={() => setTab(item.id)}
                    >
                      {`${l(item.label)} · ${data[item.id].length}`}
                    </Button>
                  ))}
                </Flex>
                <Flex
                  gap='12px'
                  flexDirection='column'
                >
                  {data[tab].map((card, index) => (
                    <PromoCard
                      key={card.copyId ?? card.promoCodeId ?? index}
                      card={card}
                      state={cardUiState(card, tab)}
                      warn={isExpiringSoon(card, tab)}
                    />
                  ))}
                </Flex>
                {tab === 'ready' && hasExpiringSoon && (
                  <Box pt='8px'>
                    <Text
                      text={l('vouchers_expiring_note')}
                      settings={{color: theme.colors['text-secondary'], fontSize: '13px'}}
                    />
                  </Box>
                )}
              </>
            )}
            {venueId && (
              <Box pt='16px'>
                <Text
                  text={l('vouchers_got_code')}
                  settings={{fontWeight: 600, fontSize: '14px'}}
                />
                <Box pt='8px'>
                  <AddCodeForm
                    venueId={venueId}
                    currency={venue?.currency}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </GridLayout>
  )
}
